import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import './TrendView.css';
import { fetchTrends, fetchThresholds } from '../services/api';
import { useSites } from '../context/SitesContext';
import ThresholdModal from '../components/ThresholdModal';

const TrendView = () => {
  const { siteId, metricType = 'load_average' } = useParams();
  const navigate = useNavigate();
  const { sites } = useSites();
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [site, setSite] = useState(null);
  const [chartHeight, setChartHeight] = useState(500);
  const [isMobile, setIsMobile] = useState(false);
  const [thresholds, setThresholds] = useState(null);
  const [showThresholdModal, setShowThresholdModal] = useState(false);

  // Metric configuration - designed for extensibility
  const metricConfig = {
    load_average: {
      label: 'Load AVG Per Camera',
      unit: 'events/hour',
      color: '#3b82f6',
      yAxisLabel: 'Load Average (events/hour)'
    },
    cpu: {
      label: 'CPU Usage',
      unit: '%',
      color: '#ef4444',
      yAxisLabel: 'CPU Usage (%)'
    },
    memory: {
      label: 'Memory Usage',
      unit: '%',
      color: '#10b981',
      yAxisLabel: 'Memory Usage (%)'
    },
    disk_space: {
      label: 'Disk Space Usage',
      unit: '%',
      color: '#f59e0b',
      yAxisLabel: 'Disk Space Usage (%)'
    }
  };

  const currentMetric = metricConfig[metricType] || metricConfig.load_average;

  useEffect(() => {
    const loadSite = () => {
      const siteData = sites.find(s => s.id === parseInt(siteId));
      if (siteData) {
        setSite(siteData);
      }
    };

    if (sites.length > 0) {
      loadSite();
    }
  }, [sites, siteId]);

  const loadTrendData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchTrends(parseInt(siteId), metricType, selectedDate);
      
      // Transform data for chart
      const chartData = data.data.map(item => ({
        time: item.timeSlot.slice(0, 5), // HH:MM format
        value: item.value !== null ? parseFloat(item.value.toFixed(2)) : null,
        sampleCount: item.sampleCount
      }));
      
      setTrendData(chartData);
    } catch (err) {
      setError('Failed to load trend data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Set chart height and mobile state based on screen size
  useEffect(() => {
    const updateChartSize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setChartHeight(450);
      } else {
        setChartHeight(500);
      }
    };

    updateChartSize();
    window.addEventListener('resize', updateChartSize);
    return () => window.removeEventListener('resize', updateChartSize);
  }, []);

  useEffect(() => {
    if (siteId) {
      loadTrendData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, metricType, selectedDate]);

  // Load thresholds for the site
  useEffect(() => {
    const loadThresholds = async () => {
      try {
        const thresholdsData = await fetchThresholds(parseInt(siteId));
        setThresholds(thresholdsData);
      } catch (err) {
        console.error('Error loading thresholds:', err);
        // Don't set error state - thresholds are optional for display
      }
    };

    if (siteId) {
      loadThresholds();
    }
  }, [siteId]);

  // Format tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="trend-tooltip">
          <p className="trend-tooltip-time">{`Time: ${data.time}`}</p>
          <p className="trend-tooltip-value">
            {currentMetric.label}: {data.value !== null ? `${data.value} ${currentMetric.unit}` : 'No data'}
          </p>
          {data.sampleCount > 0 && (
            <p className="trend-tooltip-samples">Samples: {data.sampleCount}</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Format X-axis labels to show only every 2 hours
  const formatXAxisLabel = (tickItem, index) => {
    const hour = parseInt(tickItem.split(':')[0]);
    if (hour % 2 === 0) {
      return tickItem;
    }
    return '';
  };

  if (loading) {
    return (
      <div className="trend-view-loading">
        <div className="spinner"></div>
        <p>Loading trend data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trend-view-error">
        <p>{error}</p>
        <button onClick={() => navigate(`/site/${siteId}`)} className="back-btn">
          Go Back to Site Details
        </button>
      </div>
    );
  }

  return (
    <div className="trend-view">
      <div className="trend-view-header">
        <button onClick={() => navigate(`/site/${siteId}`)} className="back-btn">
          ← Back to Site Details
        </button>
        <div className="trend-view-title-section">
          <h1 className="trend-view-title">
            {site ? `${site.name} - ${currentMetric.label} Trend` : `${currentMetric.label} Trend`}
          </h1>
          <div className="trend-view-controls">
            <label htmlFor="date-select" className="trend-view-date-label">
              Date:
            </label>
            <input
              id="date-select"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="trend-view-date-input"
              max={new Date().toISOString().slice(0, 10)}
            />
            <button
              onClick={loadTrendData}
              className="refresh-btn"
              title="Refresh trend data"
              disabled={loading}
            >
              🔄 Refresh
            </button>
            <button
              onClick={() => setShowThresholdModal(true)}
              className="threshold-settings-btn"
              title="Configure Alert Thresholds"
            >
              ⚙️ Threshold Settings
            </button>
          </div>
        </div>
      </div>

      <div className="trend-view-content">
        <div className="trend-chart-container">
          <div className={isMobile ? 'trend-chart-wrapper-mobile' : 'trend-chart-wrapper'} style={isMobile ? { width: '800px', minWidth: '800px' } : {}}>
            <ResponsiveContainer width={isMobile ? 800 : "100%"} height={chartHeight}>
            <LineChart
              data={trendData}
              margin={isMobile ? { top: 20, right: 10, left: 10, bottom: 80 } : { top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              {thresholds && thresholds.loadAverage && thresholds.loadAverage.warning != null && !isNaN(thresholds.loadAverage.warning) && (
                <ReferenceLine
                  y={thresholds.loadAverage.warning}
                  stroke="#fbbf24"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  label={{ value: "Warning", position: "right", fill: "#fbbf24", fontSize: 12 }}
                />
              )}
              {thresholds && thresholds.loadAverage && thresholds.loadAverage.critical != null && !isNaN(thresholds.loadAverage.critical) && (
                <ReferenceLine
                  y={thresholds.loadAverage.critical}
                  stroke="#ef4444"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  label={{ value: "Critical", position: "right", fill: "#ef4444", fontSize: 12 }}
                />
              )}
              <XAxis
                dataKey="time"
                stroke="#6b7280"
                tick={{ fill: '#6b7280', fontSize: isMobile ? 10 : 12 }}
                angle={isMobile ? -60 : -45}
                textAnchor="end"
                height={isMobile ? 100 : 80}
                interval={11} // Show every 2 hours (12 slots per 2 hours)
                tickFormatter={formatXAxisLabel}
              />
              <YAxis
                stroke="#6b7280"
                tick={{ fill: '#6b7280', fontSize: isMobile ? 10 : 12 }}
                label={isMobile ? { value: currentMetric.yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 10 } } : { value: currentMetric.yAxisLabel, angle: -90, position: 'insideLeft' }}
                width={isMobile ? 40 : 60}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="value"
                name={currentMetric.label}
                stroke={currentMetric.color}
                strokeWidth={isMobile ? 2.5 : 2}
                dot={{ r: isMobile ? 4 : 3, fill: currentMetric.color }}
                activeDot={{ r: isMobile ? 7 : 6 }}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
          </div>
        </div>

        <div className="trend-info">
          <div className="trend-info-item">
            <span className="trend-info-label">Metric:</span>
            <span className="trend-info-value">{currentMetric.label}</span>
          </div>
          <div className="trend-info-item">
            <span className="trend-info-label">Date:</span>
            <span className="trend-info-value">{selectedDate}</span>
          </div>
          <div className="trend-info-item">
            <span className="trend-info-label">Data Points:</span>
            <span className="trend-info-value">
              {trendData.filter(d => d.value !== null).length} / {trendData.length}
            </span>
          </div>
        </div>
      </div>

      {showThresholdModal && site && (
        <ThresholdModal
          siteId={site.id}
          siteName={site.name}
          onClose={() => setShowThresholdModal(false)}
          onSave={() => {
            setShowThresholdModal(false);
            // Reload thresholds after saving
            const loadThresholds = async () => {
              try {
                const thresholdsData = await fetchThresholds(parseInt(siteId));
                setThresholds(thresholdsData);
              } catch (err) {
                console.error('Error loading thresholds:', err);
              }
            };
            loadThresholds();
          }}
        />
      )}
    </div>
  );
};

export default TrendView;

